import { useState } from 'react';
import { motion } from 'framer-motion';
import { FaUser, FaEnvelope, FaPhone, FaSwimmer, FaPaperPlane, FaCheckCircle, FaTimesCircle, FaClock, FaWater } from 'react-icons/fa';
import { sendMastersInterest } from '../services/emailService';
import Seo from '../components/Seo';

function MastersInterest() {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    ageRange: '',
    ability: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');
  const [lastSubmissionTime, setLastSubmissionTime] = useState(0);
  const [submissionCount, setSubmissionCount] = useState(0);
  const [honeypot, setHoneypot] = useState('');
  const [formLoadedAt] = useState(() => Date.now());

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const now = Date.now();
    const timeSinceLastSubmission = now - lastSubmissionTime;

    if (timeSinceLastSubmission < 30000) {
      setSubmitStatus('rate-limited');
      return;
    }

    if (timeSinceLastSubmission > 3600000) {
      setSubmissionCount(0);
    }

    if (submissionCount >= 5) {
      setSubmitStatus('rate-limited');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('');
    setLastSubmissionTime(now);
    setSubmissionCount((prev) => prev + 1);

    try {
      await sendMastersInterest({ ...formData, website: honeypot, formLoadedAt });
      setSubmitStatus('success');
      setFormData({
        fullName: '',
        email: '',
        phone: '',
        ageRange: '',
        ability: '',
        message: ''
      });
    } catch (error) {
      console.error('Email send failed:', error);
      setSubmitStatus('error');
    }

    setIsSubmitting(false);
  };

  return (
    <main className="py-16 bg-gradient-to-b from-gray-50 to-white">
      <Seo
        title="Masters Swimming Interest"
        description="Register your interest in Masters swimming sessions at Marple Swimming Club for adult swimmers."
        path="/masters-interest"
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <FaWater className="text-4xl text-primary-600 mx-auto mb-4" />
          <h1 className="font-display text-5xl font-bold text-navy-900 mb-6">Masters &amp; Adult Swimming</h1>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
            With our move to the new Marple Community Hub pool, we're looking at introducing coached
            Masters and Adult swimming sessions on <strong>Wednesday and Friday evenings</strong> (two
            one-hour sessions a week). Register your interest below so we can gauge demand &mdash;
            this is not a booking, and it doesn't commit you to anything.
          </p>
        </motion.div>

        {/* Interest Form */}
        <motion.section
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
        >
          <div className="max-w-2xl mx-auto">
            <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-xl p-8 border-2 border-gray-200">
              {/* Honeypot - hidden from real users, bots often fill every field */}
              <div style={{ position: 'absolute', left: '-9999px', top: '-9999px' }} aria-hidden="true">
                <input
                  type="text"
                  name="website"
                  value={honeypot}
                  onChange={(e) => setHoneypot(e.target.value)}
                  tabIndex="-1"
                  autoComplete="off"
                />
              </div>

              <h2 className="font-display text-3xl font-bold text-navy-900 text-center mb-6">Register Your Interest</h2>

              {/* Status Messages */}
              {submitStatus === 'success' && (
                <div className="flex items-center justify-center gap-2 bg-green-50 border-2 border-green-500 text-green-800 p-4 rounded-xl mb-6 text-center">
                  <FaCheckCircle className="flex-shrink-0" />
                  Thank you! We've noted your interest in Masters/Adult swimming.
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="flex items-center justify-center gap-2 bg-red-50 border-2 border-red-500 text-red-800 p-4 rounded-xl mb-6 text-center">
                  <FaTimesCircle className="flex-shrink-0" />
                  Sorry, there was an error. Please try again.
                </div>
              )}

              {submitStatus === 'rate-limited' && (
                <div className="flex items-center justify-center gap-2 bg-amber-50 border-2 border-amber-500 text-amber-800 p-4 rounded-xl mb-6 text-center">
                  <FaClock className="flex-shrink-0" />
                  Please wait before submitting another form.
                </div>
              )}

              {/* Name */}
              <div className="mb-4">
                <label className="block text-gray-700 font-semibold mb-2">
                  <FaUser className="inline mr-2 text-primary-600" />
                  Full Name *
                </label>
                <input
                  type="text"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-primary-500 focus:outline-none transition-colors"
                />
              </div>

              {/* Email */}
              <div className="mb-4">
                <label className="block text-gray-700 font-semibold mb-2">
                  <FaEnvelope className="inline mr-2 text-primary-600" />
                  Email *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-primary-500 focus:outline-none transition-colors"
                />
              </div>

              {/* Phone */}
              <div className="mb-4">
                <label className="block text-gray-700 font-semibold mb-2">
                  <FaPhone className="inline mr-2 text-primary-600" />
                  Contact Phone Number
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-primary-500 focus:outline-none transition-colors"
                />
              </div>

              {/* Age Range */}
              <div className="mb-4">
                <label className="block text-gray-700 font-semibold mb-2">Age Range *</label>
                <select
                  name="ageRange"
                  value={formData.ageRange}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-primary-500 focus:outline-none transition-colors"
                >
                  <option value="">Please select...</option>
                  <option value="18-24">18-24</option>
                  <option value="25-34">25-34</option>
                  <option value="35-44">35-44</option>
                  <option value="45-54">45-54</option>
                  <option value="55-64">55-64</option>
                  <option value="65+">65+</option>
                </select>
              </div>

              {/* Ability */}
              <div className="mb-4">
                <label className="block text-gray-700 font-semibold mb-2">
                  <FaSwimmer className="inline mr-2 text-primary-600" />
                  Swimming Ability *
                </label>
                <select
                  name="ability"
                  value={formData.ability}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-primary-500 focus:outline-none transition-colors"
                >
                  <option value="">Please select...</option>
                  <option value="beginner">Beginner</option>
                  <option value="improver">Improver</option>
                  <option value="confident">Confident swimmer</option>
                  <option value="competitive">Competitive / former competitive</option>
                </select>
              </div>

              {/* Message */}
              <div className="mb-6">
                <label className="block text-gray-700 font-semibold mb-2">Anything Else? (optional)</label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows="4"
                  placeholder="Any other information that would help us plan Masters/Adult sessions..."
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-primary-500 focus:outline-none transition-colors"
                ></textarea>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-primary-600 text-white py-4 px-6 rounded-full font-bold text-lg hover:bg-primary-700 disabled:bg-gray-400 transition-all duration-300 shadow-lg flex items-center justify-center gap-2"
              >
                <FaPaperPlane />
                {isSubmitting ? 'Sending...' : 'Register Interest'}
              </button>
            </form>
          </div>
        </motion.section>
      </div>
    </main>
  );
}

export default MastersInterest;
